import { API_METHODS } from '@compass/client/constants/api-methods';
import type { Asset } from '@compass/client/types/asset';

import { apiClient, FILE_UPLOAD_TIMEOUT, getApiPath } from './api.config';

/**
 * Call Console (JNJ-194): the files the AI agent stored for one call. Both
 * endpoints need the global call_console permission (Global Console Viewer role).
 *
 * Downloads go through `apiClient` rather than the generated service because they
 * read the raw body (text or bytes), not a JSON message.
 */

export interface CallConsoleContext {
  organizationId: string;
  programId: string;
  caseRecordId: string;
  callId: string;
}

const pathParams = (ctx: CallConsoleContext) => ({
  organization_id: ctx.organizationId,
  program_id: ctx.programId,
  case_record_id: ctx.caseRecordId,
  call_id: ctx.callId,
});

const downloadPath = (ctx: CallConsoleContext, assetId: string) =>
  getApiPath(API_METHODS.AssetService.DownloadCallConsoleAsset, {
    ...pathParams(ctx),
    asset_id: assetId,
  });

export const callConsoleService = {
  async listAssets(ctx: CallConsoleContext): Promise<Asset[]> {
    const response = await apiClient.get<{ assets?: Asset[] }>(
      getApiPath(API_METHODS.AssetService.ListCallConsoleAssets, pathParams(ctx)),
    );
    return response.data?.assets ?? [];
  },

  /** Whole file as text. Arcus serves ranges only before upload, so console files are read whole. */
  async downloadText(ctx: CallConsoleContext, assetId: string): Promise<string> {
    const response = await apiClient.get<string>(downloadPath(ctx, assetId), {
      responseType: 'text',
      timeout: FILE_UPLOAD_TIMEOUT,
    });
    return typeof response.data === 'string' ? response.data : '';
  },

  async downloadBytes(ctx: CallConsoleContext, assetId: string): Promise<ArrayBuffer> {
    const response = await apiClient.get<ArrayBuffer>(downloadPath(ctx, assetId), {
      responseType: 'arraybuffer',
      timeout: FILE_UPLOAD_TIMEOUT,
    });
    return response.data;
  },
};
